import {
  isSelfSupplyEligible,
  type SelfSupplyEligibleMarket,
} from "./selfSupplyEligibility";

type SimplifiedPresentationFlagValue = {
  targets?: unknown;
  createdDate?: unknown;
};

const parseSimplifiedPresentationFlagValue = (
  value: unknown
): SimplifiedPresentationFlagValue | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value) as SimplifiedPresentationFlagValue;
    } catch {
      return undefined;
    }
  }

  if (typeof value === "object" && value !== null) {
    return value as SimplifiedPresentationFlagValue;
  }

  return undefined;
};

const getSimplifiedPresentationTargets = (
  value: unknown
): SelfSupplyEligibleMarket[] => {
  const record = parseSimplifiedPresentationFlagValue(value);
  const targets = record?.targets;
  if (!Array.isArray(targets)) {
    return [];
  }

  return targets.filter(
    (entry): entry is SelfSupplyEligibleMarket =>
      typeof entry === "object" &&
      entry !== null &&
      "state" in entry &&
      "utility" in entry &&
      typeof (entry as SelfSupplyEligibleMarket).state === "string" &&
      typeof (entry as SelfSupplyEligibleMarket).utility === "string"
  );
};

const getSimplifiedPresentationCreatedDateCutoff = (
  value: unknown
): string | undefined => {
  const createdDate = parseSimplifiedPresentationFlagValue(value)?.createdDate;
  if (typeof createdDate !== "string" || !createdDate.trim()) {
    return undefined;
  }

  const parsed = Date.parse(createdDate);
  if (Number.isNaN(parsed)) {
    return undefined;
  }

  return createdDate;
};

const isOfferCreatedAtEligibleForCutoff = (
  createdAt: string | undefined,
  cutoff: string | undefined
): boolean => {
  if (!cutoff) {
    return true;
  }

  if (!createdAt?.trim()) {
    return false;
  }

  const createdAtTime = Date.parse(createdAt);
  const cutoffTime = Date.parse(cutoff);

  if (Number.isNaN(createdAtTime) || Number.isNaN(cutoffTime)) {
    return false;
  }

  return createdAtTime >= cutoffTime;
};

const isSimplifiedPresentationMarketEligible = (
  state: string | undefined,
  utility: string | undefined,
  targets: SelfSupplyEligibleMarket[]
): boolean => {
  if (!state?.trim() || !utility?.trim() || targets.length === 0) {
    return false;
  }

  return isSelfSupplyEligible(state, utility, targets);
};

export {
  getSimplifiedPresentationCreatedDateCutoff,
  getSimplifiedPresentationTargets,
  isOfferCreatedAtEligibleForCutoff,
  isSimplifiedPresentationMarketEligible,
};
